import { useState, useEffect, Fragment } from "react";
import type { IPhoneInputProps } from "libs/design-system/src/types/phone_input";
import { Input, Label, PhoneInput } from "@unified-checkout/design-system";

import type { IInputsGroup } from "@app/dashboard/types/innerLayout/inputs-group";
import { useLangStore } from "@app/dashboard/store/lang";
import styles from "./inputs-group.module.scss";

type TInputsCount = Record<string, number>;

const defaultLocalization = {
  en: {
    invalidPhone: 'Please enter a valid phone number',
    maxCharLimitExceeded: 'Maximum character limit exceeded',
    character: 'character'
  },
  ar: {
    invalidPhone: 'يرجى إدخال رقم هاتف صحيح.',
    maxCharLimitExceeded: 'تم تجاوز الحد الأقصى لعدد الأحرف',
    character: 'حرف'
  }
};

const isPhoneInput = (item: IInputsGroup['inputsProps'][number]): item is IPhoneInputProps => {
  return (item as IPhoneInputProps)?.onChangeHandler !== undefined;
};

export const InputsGroup = ({
  label,
  width,
  inputsProps,
  error,
  maxCharacters = 50,
  localization,
  onMaxCharsErrorHandler
}: IInputsGroup) => {
  const { lang } = useLangStore();

  const TEXT = { ...defaultLocalization[lang], ...(localization || {}) };

  const [totalCharsCount, setTotalCharsCount] = useState<number>(0);
  const [inputsCount, setInputsCount] = useState<TInputsCount>({});
  const [errMsg, setErrMsg] = useState('');

  useEffect(() => {
    const initialCounts: TInputsCount = {};

    inputsProps.forEach((input, index) => {
      if (!isPhoneInput(input) && input.value) {
        initialCounts[`${index}-${input.name}`] = input.value.length;
      }
    });

    setInputsCount(initialCounts);

  }, [inputsProps]);

  useEffect(() => {
    setTotalCharsCount(() => {
      const newCount = Object.values(inputsCount).reduce((acc, count) => acc + count, 0);
      return newCount;
    });
  }, [inputsCount]);

  useEffect(() => {
    setErrMsg((totalCharsCount > maxCharacters) ? TEXT.maxCharLimitExceeded : '');
  }, [totalCharsCount, maxCharacters]);

  useEffect(() => {
    onMaxCharsErrorHandler?.(errMsg);
  }, [errMsg]);

  return (
    <div className={styles['inputs-group']} style={{ width: width || '100%' }}>
      {label && <Label text={label} />}

      <div className={styles['inputs-group__box']}>
        {
          inputsProps.map((input, index) => (
            <Fragment key={`inputs-group-fragment-${index}`}>
              {
                isPhoneInput(input) ?
                  <PhoneInput {...input} />
                  :
                  <Input
                    {...input}
                    onChange={(e) => {
                      input.onChange?.(e);

                      setInputsCount(prevCounts => ({
                        ...prevCounts,
                        [`${index}-${input.name}`]: e.target.value.length
                      }));
                    }}
                  />
              }
            </Fragment>
          ))
        }
      </div>

      <div
        className={styles['inputs-group__chars-count']}
        data-input-group-chars-count-exceeded={totalCharsCount > maxCharacters}
      >
        {totalCharsCount}/{maxCharacters}
        {" "}
        {TEXT.character}
      </div>

      {
        (errMsg || error) &&
        <div className={styles['inputs-group__err-msg']}>
          {errMsg || error}
        </div>
      }

    </div>
  )
}